import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { VendorState } from "../../../../redux/vendor/rootReducer";
import { useTranslation } from "react-i18next";
import { StackBondRequirements } from "../models/StackBondRequirements.model";
import { useTextureApi } from "../../../../common/hooks/use-texture-api.hook";
import { setLoading } from "../../../../redux/vendor/ui/uiActions";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    GroutColorField,
    PatternFieldLabel,
    PatternSectionTitle,
    SliderWithInput,
} from "./PatternFormControls";

export const StackBondPatternForm = ({seamlessResult, setSeamlessResult, uploadedImages = []}) => {
  const { t } = useTranslation();
  const { language } = useSelector((state: VendorState) => state.settings.theme);
  const [isOnEditGroutColor, setIsOnEditGroutColor] = useState(false);
  const [randomize, setRandomize] = useState(true);
  const [mirror, setMirror] = useState(false);
  const textureAPI = useTextureApi();
  const dispatch = useDispatch();
  const [fields, setFields] = useState({
      plankWidth: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.PLANK_WIDTH')}`,
          name: "plankWidth",
          value: "10",
          unit: "cm"
      },
      plankHeight: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.PLANK_HEIGHT')}`,
          name: "plankHeight",
          value: "20",
          unit: "cm"
      },
      textureWidth: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.TEXTURE_WIDTH')}`,
          name: "textureWidth",
          value: "2",
          unit: "m"
      },
      textureHeight: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.TEXTURE_HEIGHT')}`,
          name: "textureHeight",
          value: "2",
          unit: "m"
      },
      groutLine: {
        isTouched: false,
        label: `${t('PATTERNS.PARTIALS.GROUT_LINE_WIDTH')}`,
        name: "groutLine",
        value: "2",
        unit: "mm"
      },
      groutColor: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.GROUT_LINE_COLOR')}`,
          name: "groutColor",
          value: "#CCCCCC"
      }
  });

  const handleChange = (event) => {
      const { name, value } = event.target;
      setFields({...fields, [name]: {
          ...fields[name],
          value: value,
          isTouched: true
      }});
  };

  const handleSliderChange = (name, value) => {
    setFields({...fields, [name]: {
      ...fields[name],
      value: value.toString(),
      isTouched: true
    }});
  };

  const isValidForm = () => {
    return uploadedImages && uploadedImages.length > 0 &&
      fields.plankWidth.value &&
      fields.plankHeight.value &&
      fields.textureWidth.value &&
      fields.textureHeight.value;
  };

  const handleGenerateSeamless = async () => {
    if(isValidForm()){
      try {
        dispatch(setLoading(true));
        const body = new StackBondRequirements();
        body.setFiles(uploadedImages)
            .setPlankWidth(fields.plankWidth.value)
            .setPlankHeight(fields.plankHeight.value)
            .setTextureWidth(fields.textureWidth.value)
            .setTextureHeight(fields.textureHeight.value)
            .setGroutLineColor(fields.groutColor.value || "#FFFFFF")
            .setGroutLineWidth(fields.groutLine.value)
            .setRandomize(randomize)
            .setMirror(mirror);

        let data = await textureAPI.generateTextureForPattern(body, "stackbond");
        setSeamlessResult(data);
      } catch (error) {
        console.error('Error generating seamless texture:', error);
        setSeamlessResult(null);
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

  const handleGroutColorChange = (color) => {
    setFields({
      ...fields,
      groutColor: {
        ...fields.groutColor,
        value: color.hex + Math.round(color.rgb.a * 255).toString(16)
      }
    });
  };

  return (
    <div>
      {/* Hidden generate button that can be triggered programmatically */}
      <button
        type="button"
        id="stackbond-generate-btn"
        onClick={handleGenerateSeamless}
        className="hidden"
      />

      <div className="mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.DIMENSIONS')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.plankWidth.label}</PatternFieldLabel>
            <SliderWithInput
              name="plankWidth"
              value={fields.plankWidth.value}
              unit={fields.plankWidth.unit}
              min={5}
              max={50}
              step={1}
              onSliderChange={handleSliderChange}
              onInputChange={handleChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.plankHeight.label}</PatternFieldLabel>
            <SliderWithInput
              name="plankHeight"
              value={fields.plankHeight.value}
              unit={fields.plankHeight.unit}
              min={5}
              max={100}
              step={1}
              onSliderChange={handleSliderChange}
              onInputChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className="mt-8 mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.TEXTURE')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.textureWidth.label}</PatternFieldLabel>
            <SliderWithInput
              name="textureWidth"
              value={fields.textureWidth.value}
              min={1}
              max={4}
              step={1}
              markLabels={['1m', '2m', '3m', '4m']}
              showInput={false}
              onSliderChange={handleSliderChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.textureHeight.label}</PatternFieldLabel>
            <SliderWithInput
              name="textureHeight"
              value={fields.textureHeight.value}
              min={1}
              max={4}
              step={1}
              markLabels={['1m', '2m', '3m', '4m']}
              showInput={false}
              onSliderChange={handleSliderChange}
            />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.GROUT')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.groutLine.label}</PatternFieldLabel>
            <SliderWithInput
              name="groutLine"
              value={fields.groutLine.value}
              unit={fields.groutLine.unit}
              min={0}
              max={10}
              step={0.5}
              onSliderChange={handleSliderChange}
              onInputChange={handleChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.groutColor.label}</PatternFieldLabel>
            <GroutColorField
              color={fields.groutColor.value}
              open={isOnEditGroutColor}
              onOpenChange={setIsOnEditGroutColor}
              onColorChange={handleGroutColorChange}
            />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.OPTIONS', 'Options')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Label className="flex items-start gap-3 font-normal">
            <Switch checked={randomize} onCheckedChange={setRandomize} className="mt-0.5" />
            <span>
              <span className="block text-sm text-foreground">
                {t('PATTERNS.PARTIALS.RANDOMIZE', 'Randomize')}
              </span>
              <span className="block text-xs text-muted-foreground">
                {t('PATTERNS.PARTIALS.RANDOMIZE_HINT', 'When off, tiles are placed in upload order')}
              </span>
            </span>
          </Label>

          <Label className="flex items-start gap-3 font-normal">
            <Switch checked={mirror} onCheckedChange={setMirror} className="mt-0.5" />
            <span>
              <span className="block text-sm text-foreground">
                {t('PATTERNS.PARTIALS.MIRROR', 'Mirror (2x2)')}
              </span>
              <span className="block text-xs text-muted-foreground">
                {t('PATTERNS.PARTIALS.MIRROR_HINT', 'Apply 2x2 mirror flip effect on tiles')}
              </span>
            </span>
          </Label>
        </div>
      </div>
    </div>
  );
};
